import { useEffect, useState } from "react";
import { CheckCircle2, ChevronDown, FileText, Mail, RotateCcw, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AuthImg, P, StatusPill, openPdf } from "@/components/studio/portalKit";
import { PromptDialog } from "@/components/desk/PromptDialog";

const FILTERS = {
  "plan-pay": ["Awaiting Payment Approval", "Payment Approved", "Payment Rejected", ""],
  warranty: ["Submitted", "Registered", "Sent", "Returned", ""],
  plan: ["Payment Approved", "Awaiting Payment Approval", "Registered", "Sent", "Returned", ""],
};

const Thumbs = ({ label, list }) => (list?.length ? (
  <div><p className="mb-1 text-xs font-semibold text-slate-500">{label}</p>
    <div className="flex flex-wrap gap-2">{list.map((p) => (p.endsWith(".pdf") ? <span key={p} className="rounded bg-slate-100 px-2 py-1 text-xs">PDF</span> : <AuthImg key={p} path={p} className="h-16 w-16 rounded" />))}</div>
  </div>) : null);

const Details = ({ s }) => (
  <div className="grid gap-3 rounded-lg bg-slate-50 p-3 text-sm sm:grid-cols-2">
    <p><b>Customer:</b> {s.customer.name} · {s.customer.mobile} · {s.customer.email}{s.customer.address && ` · ${s.customer.address}`}</p>
    <p><b>Vehicle:</b> {s.vehicle.make} {s.vehicle.model} · {s.vehicle.reg_no} · {s.vehicle.colour || "—"}{s.vehicle.vin && ` · VIN ${s.vehicle.vin}`}</p>
    <p><b>Product:</b> {s.brand} · {s.product} · {s.brand === "Velotint" ? (s.films?.length ? s.films.map((f) => `${f.vlt} on ${f.glass.join(", ")} (${f.sqft} sqft${f.serial ? `, ${f.serial}` : ""})`).join(" · ") : `VLT ${s.vlt} · ${s.glass.join(", ")}`) : `${s.coverage}${s.coverage_panels ? ` (${s.coverage_panels})` : ""}`}</p>
    <p><b>Install:</b> {s.install_date} · {s.installer_name} · {s.sqft} sqft · {s.rolls?.length > 1 ? s.rolls.map((r) => `${r.product ? `${r.product}: ` : ""}${r.serial || "—"} (${r.sqft} sqft)`).join(" + ") : `Roll ${s.serial || "—"}`}</p>
    <p><b>Studio email:</b> {s.studio_email || "—"}</p>
    {s.kind === "plan" && <p><b>Payment:</b> {s.plan_type} · {s.payment_mode} · Ref {s.payment_reference || "—"}</p>}
    <Thumbs label="Before" list={s.photos?.before} /><Thumbs label="After" list={s.photos?.after} />
    <Thumbs label="Invoice" list={s.photos?.invoice} /><Thumbs label="Payment proof" list={s.payment_proof} />
    <div className="sm:col-span-2"><p className="text-xs text-slate-500">{(s.history || []).map((h) => `${h.action} by ${h.by} ${fmtDate(h.at)}${h.note ? ` (${h.note})` : ""}`).join(" → ")}</p></div>
  </div>
);

const Actions = ({ s, mode, open, run }) => {
  const base = `${P}/central/${s.kind === "plan" ? "plans" : "warranties"}/${s.id}`;
  const st = s.status;
  if (mode === "plan-pay") return st === "Awaiting Payment Approval" ? (<>
    <Button size="sm" data-testid={`approve-pay-${s.id}`} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => open(s, "approve")}><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Payment Received</Button>
    <Button size="sm" variant="ghost" data-testid={`reject-pay-${s.id}`} className="text-red-600" onClick={() => open(s, "reject")}><XCircle className="mr-1 h-3.5 w-3.5" />Reject</Button>
  </>) : null;
  const canReg = mode === "plan" ? st === "Payment Approved" : st === "Submitted";
  return (<>
    {!["Registered", "Sent"].includes(st) && (
      <Button size="sm" data-testid={`register-${s.id}`} disabled={!canReg} onClick={() => run(() => api.post(`${base}/register`), "Registered — certificate generated")} className="bg-brand-deep">
        <ShieldCheck className="mr-1 h-3.5 w-3.5" />{mode === "plan" && st === "Awaiting Payment Approval" ? "Register (awaiting accountant)" : "Register"}
      </Button>
    )}
    {canReg && <Button size="sm" variant="outline" data-testid={`return-${s.id}`} onClick={() => open(s, "return")}><RotateCcw className="mr-1 h-3.5 w-3.5" />Return to Studio</Button>}
    {["Registered", "Sent"].includes(st) && (<>
      <Button size="sm" data-testid={`send-${s.id}`} className="bg-crimson-500 hover:bg-crimson-600" onClick={() => run(() => api.post(`${base}/send`), "Emailed to customer and studio")}><Mail className="mr-1 h-3.5 w-3.5" />{st === "Sent" ? "Resend" : "Send"}</Button>
      <Button size="sm" variant="outline" data-testid={`doc-${s.id}`} onClick={() => openPdf(`${P}/submissions/${s.id}/document.pdf`)}><FileText className="mr-1 h-3.5 w-3.5" />{s.kind === "plan" ? "Plan document" : "Certificate"}</Button>
    </>)}
  </>);
};

export const DeskQueue = ({ mode, onChange }) => {
  const kind = mode === "warranty" ? "warranty" : "plan";
  const [status, setStatus] = useState(FILTERS[mode][0]);
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [dlg, setDlg] = useState(null);
  const load = () => api.get(`${P}/central/submissions`, { params: { kind, status: status || undefined } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [status, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn, msg) => {
    try { const { data } = await fn(); if (data?.email && !data.email.sent) toast.warning(`Email: ${data.email.reason}`); else toast.success(msg); setDlg(null); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const base = dlg && `${P}/central/${kind === "plan" ? "plans" : "warranties"}/${dlg.s.id}`;
  const cfg = dlg && {
    approve: { title: "Plan payment received", confirm: "Approve", initial: { date_received: new Date().toISOString().slice(0, 10), payment_mode: dlg.s.payment_mode, payment_reference: dlg.s.payment_reference },
      fields: [{ key: "date_received", label: "Date received", type: "date", required: true }, { key: "payment_mode", label: "Mode" }, { key: "payment_reference", label: "Reference" }],
      submit: (v) => run(() => api.post(`${base}/approve`, v), "Payment approved — sent to Warranty CRM") },
    reject: { title: "Reject payment", confirm: "Reject", fields: [{ key: "reason", label: "Reason", required: true }], submit: (v) => run(() => api.post(`${base}/reject`, v), "Rejected — studio notified") },
    return: { title: "Return to studio", confirm: "Return", fields: [{ key: "reason", label: "Reason", required: true }], submit: (v) => run(() => api.post(`${base}/return`, v), "Returned — studio notified") },
  }[dlg.kind];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {FILTERS[mode].map((s) => (
          <button key={s || "all"} data-testid={`${mode}-filter-${s || "all"}`} onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${status === s ? "bg-brand-deep text-white" : "bg-slate-100 text-slate-600"}`}>{s || "All"}</button>
        ))}
      </div>
      {rows.length === 0 && <p className="text-sm text-slate-500">Nothing in this queue.</p>}
      {rows.map((s) => (
        <Card key={s.id} data-testid={`queue-row-${s.id}`}>
          <CardContent className="space-y-3 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button className="text-left" onClick={() => setOpenId(openId === s.id ? null : s.id)} data-testid={`queue-expand-${s.id}`}>
                <p className="font-semibold text-brand-ink">{s.studio_name} · {s.customer.name} · {s.vehicle.reg_no} <ChevronDown className="inline h-4 w-4" /></p>
                <p className="text-xs text-slate-500">{s.brand} · {s.product} · {fmtDate(s.updated_at)}{s.certificate_no && ` · No. ${s.certificate_no}`}</p>
              </button>
              <StatusPill status={s.status} testid={`queue-status-${s.id}`} />
            </div>
            {(s.return_reason || s.reject_reason) && ["Returned", "Payment Rejected"].includes(s.status) && <p className="text-xs text-red-600">Reason: {s.return_reason || s.reject_reason}</p>}
            {openId === s.id && <Details s={s} />}
            <div className="flex flex-wrap gap-2"><Actions s={s} mode={mode} open={(x, k) => setDlg({ s: x, kind: k })} run={run} /></div>
          </CardContent>
        </Card>
      ))}
      {cfg && <PromptDialog open title={cfg.title} fields={cfg.fields} initial={cfg.initial || {}} confirm={cfg.confirm} onClose={() => setDlg(null)} onSubmit={cfg.submit} />}
    </div>
  );
};
